import type { CourseCashTransaction } from 'tio-common/types/course-cash-transaction';
import type TasAssistanceModel from 'tio-common/models/tas-assistance';
import type { TransitionLogSignature } from 'tio-common/types/tuition-assistance';

/**
 * Transaction Adapter
 *
 * Transforms backend TasAssistance models into the UI's CourseCashTransaction shape.
 * This adapter provides a single point to map from TasAssistance records with
 * CAPTURED state to the common transaction interface.
 */

/**
 * Map TasAssistance records (Course Cash with CAPTURED state) to UI transaction shape.
 *
 * @param assistances - Array of TasAssistance models from the application
 * @returns Array of CourseCashTransaction for UI consumption
 */
export function mapAssistancesToTransactions(
  assistances: TasAssistanceModel[]
): CourseCashTransaction[] {
  return assistances
    .filter((a) => a.isCourseCash && a.isCaptured && a.id)
    .map((assistance) => {
      const receiptStatus = determineReceiptStatus(assistance);
      const capturedDate = assistance.capturedDate;
      const dueDate = assistance.receiptDueDate;

      return {
        id: assistance.id!,
        transactionDate: capturedDate?.toISOString().split('T')[0] ?? '',
        merchantName: extractMerchantName(assistance),
        amount: Math.abs(assistance.amount ?? 0), // Keep in cents, remove negative
        receiptStatus,
        receiptDueDate: dueDate?.toISOString().split('T')[0],
        receiptUrl: assistance.tasAssets?.[0]?.signedUrl,
        description: extractDescription(assistance),
        category: 'Education',
        assistanceId: assistance.id!,
        lineItems: [],
      };
    });
}

/**
 * Determine the receipt status based on CARD_USAGE_PROOF attachment presence and future verification state
 */
function determineReceiptStatus(
  assistance: TasAssistanceModel
): CourseCashTransaction['receiptStatus'] {
  // Use the model's hasCardUsageProof getter which checks attachments for CARD_USAGE_PROOF classification
  if (!assistance.hasCardUsageProof) {
    return 'needed';
  }

  // Future: Check asset verification status when backend supports it
  // For now, treat any uploaded CARD_USAGE_PROOF asset as "uploaded" pending verification
  return 'uploaded';
}

/**
 * Extract merchant name from assistance transition log or fields
 */
function extractMerchantName(assistance: TasAssistanceModel): string {
  // The merchant name may be stored in the instrument's data or transition log
  const transitionLog = assistance.transitionLog?.find(
    (log: TransitionLogSignature) => log.to === 'TAS.AssistanceState.CAPTURED'
  );

  // Check if merchant info is in transition reason
  // @ts-expect-error - merchant may exist in reason object
  const merchantFromLog = transitionLog?.reason?.merchant?.name;
  if (merchantFromLog) return merchantFromLog as string;

  // Fallback to fields if available
  const merchantFromFields = assistance.fields?.merchantName;
  if (merchantFromFields) return merchantFromFields as string;

  return 'Merchant';
}

/**
 * Extract transaction description
 */
function extractDescription(assistance: TasAssistanceModel): string | undefined {
  return assistance.fields?.description as string | undefined;
}

/**
 * Transforms PcsAuthorization model to CourseCashTransaction
 *
 * @deprecated Use mapAssistancesToTransactions instead - this is a placeholder
 * @param authorization - The PcsAuthorization model from Ember Data
 * @returns CourseCashTransaction - Transformed transaction data
 */
export function fromPcsAuthorization(authorization: unknown): CourseCashTransaction {
  // TODO: Replace with actual model mapping when PcsAuthorization is available
  // This is a placeholder structure to show expected transformation
  const auth = authorization as Record<string, unknown>;
  return {
    id: (auth.id as string) || '',
    transactionDate: (auth.createdAt as string) || new Date().toISOString(),
    merchantName: (auth.merchantName as string) || 'Merchant',
    amount: parseFloat((auth.amount as string) || '0'),
    receiptStatus: mapReceiptStatus(auth.receiptStatus as string | undefined),
    receiptDueDate: auth.receiptDueDate as string | undefined,
    receiptUrl: auth.receiptUrl as string | undefined,
    description: auth.description as string | undefined,
    category: auth.category as string | undefined,
    cardId: auth.cardId as string | undefined,
    lineItems: [],
  };
}

/**
 * Maps backend receipt status to UI receipt status
 */
function mapReceiptStatus(
  backendStatus: string | undefined
): CourseCashTransaction['receiptStatus'] {
  switch (backendStatus?.toLowerCase()) {
    case 'needed':
    case 'pending':
      return 'needed';
    case 'uploaded':
    case 'submitted':
      return 'uploaded';
    case 'verified':
    case 'approved':
      return 'verified';
    case 'not_required':
    case 'not-required':
    case 'na':
      return 'not_required';
    default:
      return 'needed'; // Default to needed if status is unclear
  }
}

/**
 * Filters transactions that need receipt upload
 */
export function getUnverifiedTransactions(
  transactions: CourseCashTransaction[]
): CourseCashTransaction[] {
  return transactions.filter((t) => t.receiptStatus === 'needed');
}

/**
 * Groups transactions by receipt due date
 */
export function groupByDueDate(
  transactions: CourseCashTransaction[]
): Map<string, CourseCashTransaction[]> {
  const grouped = new Map<string, CourseCashTransaction[]>();

  for (const transaction of transactions) {
    const dueDate = transaction.receiptDueDate || 'No due date';
    const existing = grouped.get(dueDate) || [];
    existing.push(transaction);
    grouped.set(dueDate, existing);
  }

  return grouped;
}

/**
 * Sorts transactions by date (newest first)
 */
export function sortByDate(transactions: CourseCashTransaction[]): CourseCashTransaction[] {
  return [...transactions].sort((a, b) => {
    const dateA = new Date(a.transactionDate).getTime();
    const dateB = new Date(b.transactionDate).getTime();
    return dateB - dateA; // Newest first
  });
}
